import flatpickr from "flatpickr";
import { Options } from "flatpickr/dist/types/options";
import { CustomLocale } from "flatpickr/dist/types/locale";
import { Instance } from "flatpickr/dist/types/instance";
import { Txt } from "../../../common/text";

/**
 * Shared helpers for the flatpickr-based date and date-range edit controls:
 * common formats, locale built from the app's own translations, the base
 * flatpickr options both pickers reuse, and the conversions between the
 * application's DD.MM.YYYY strings and the native input's ISO value.
 */
export class DatePickerHelper
{
    /** Format used throughout the application. */
    static readonly FLATPICKR_FORMAT = "d.m.Y";

    /**
     * Builds a flatpickr locale from the application's translations (Txt), so the
     * calendar always matches the user's selected app language instead of relying
     * on the browser locale or flatpickr's bundled l10n files. Txt only carries a
     * single variant of each name, so it is reused for the short and long forms.
     */
    static getLocale(): CustomLocale
    {
        const months = Txt.monthNames;
        const weekdays = Txt.dayNamesShort;

        // Cast through unknown because flatpickr types the name arrays as
        // fixed-length tuples while Txt provides plain string[].
        return {
            firstDayOfWeek: 1,
            rangeSeparator: " - ",
            weekdays: { shorthand: weekdays, longhand: weekdays },
            months: { shorthand: months, longhand: months }
        } as unknown as CustomLocale;
    }

    /** flatpickr options shared by both pickers. */
    static baseOptions(): Partial<Options>
    {
        return {
            dateFormat: DatePickerHelper.FLATPICKR_FORMAT,
            locale: DatePickerHelper.getLocale(),
            disableMobile: true,
            // Render the calendar inline inside the input's wrapper instead of
            // appending it to <body>. The app's dialogs use a native <dialog>
            // opened with showModal(), which puts them in the browser's top
            // layer; a body-level calendar sits behind that layer and can't be
            // clicked. Static mode keeps the calendar inside the dialog DOM so it
            // stays interactive and, being part of the dialog's own scrollable
            // content, scrolls together with it.
            static: true,
            // Static mode disables flatpickr's horizontal repositioning, so a
            // wide calendar (the range picker) anchored at the field could spill
            // past the dialog and be clipped by its overflow. Pull it back in.
            onOpen: (_dates, _str, instance) => DatePickerHelper.clampToViewport(instance)
        };
    }

    /**
     * Shifts the (statically positioned) calendar left when it would otherwise
     * extend past the right edge of the viewport, so the wide range calendar is
     * never cut off in a narrow dialog or a right-hand column.
     */
    static clampToViewport(instance: Instance): void
    {
        const cal = instance.calendarContainer;
        if (!cal)
        {
            return;
        }

        // Measure after the calendar has been laid out as "open".
        window.requestAnimationFrame(() =>
        {
            cal.style.left = "";
            const overflowRight = cal.getBoundingClientRect().right - (window.innerWidth - 4);
            if (overflowRight > 0)
            {
                cal.style.left = `-${Math.ceil(overflowRight)}px`;
            }
        });
    }

    /** A flatpickr Date -> DD.MM.YYYY string. */
    static format(date: Date): string
    {
        return flatpickr.formatDate(date, DatePickerHelper.FLATPICKR_FORMAT);
    }

    /** DD.MM.YYYY -> YYYY-MM-DD for the native input value. */
    static toNative(value: string): string
    {
        const match = value?.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (!match)
        {
            return "";
        }
        return match[3] + "-" + match[2] + "-" + match[1];
    }

    /** YYYY-MM-DD (native input value) -> DD.MM.YYYY for the application. */
    static fromNative(value: string): string
    {
        const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match)
        {
            return null;
        }
        return match[3] + "." + match[2] + "." + match[1];
    }
}
